/**
 * Centralized event name constants for Amplitude analytics
 *
 * Benefits:
 * - Type safety and autocomplete
 * - Prevents typos in event names
 * - Single source of truth for analytics taxonomy
 * - Easy to maintain and update
 *
 * Naming convention: Object + Action
 * Example: "Case Created", "Document Uploaded"
 */

export const ANALYTICS_EVENTS = {
  // ============================================
  // Authentication Events
  // ============================================
  AUTH_LOGIN_SUCCESS: 'Auth - Login Success',
  AUTH_LOGIN_FAILED: 'Auth - Login Failed',
  AUTH_LOGOUT: 'Auth - Logout',
  AUTH_OAUTH_CALLBACK_SUCCESS: 'Auth - OAuth Callback Success',
  AUTH_OAUTH_CALLBACK_FAILED: 'Auth - OAuth Callback Failed',
  AUTH_SESSION_EXPIRED: 'Auth - Session Expired',

  // ============================================
  // Page View Events
  // ============================================
  PAGE_VIEWED: 'Page Viewed',

  // ============================================
  // Case Management Events
  // ============================================
  CASE_CREATED: 'Case Created',
  CASE_UPDATED: 'Case Updated',
  CASE_DELETED: 'Case Deleted',
  CASE_VIEWED: 'Case Viewed',
  CASE_CREATION_STARTED: 'Case Creation Started',
  CASE_CREATION_FAILED: 'Case Creation Failed',
  CASE_INFO_TOGGLED: 'Case Info Toggled',
  CASE_APPLICANT_NAME_UPDATED: 'Applicant Name Updated',
  CASE_LIST_SORTED: 'Case List Sorted',
  CASE_LIST_VIEW_MODE_CHANGED: 'Case List View Mode Changed',

  // ============================================
  // Case Navigation Events
  // ============================================
  TAB_CHANGED: 'Tab Changed',
  CASE_SELECTED: 'Case Selected',

  // ============================================
  // Document Events
  // ============================================
  DOCUMENT_UPLOAD_STARTED: 'Document Upload Started',
  DOCUMENT_UPLOADED: 'Document Uploaded',
  DOCUMENT_UPLOAD_FAILED: 'Document Upload Failed',
  DOCUMENT_DOWNLOADED: 'Document Downloaded',
  DOCUMENT_DELETED: 'Document Deleted',
  DOCUMENT_RENAMED: 'Document Renamed',
  DOCUMENT_MOVED: 'Document Moved',
  DOCUMENT_PREVIEWED: 'Document Previewed',
  FOLDER_SELECTED: 'Folder Selected',
  FOLDER_CREATED: 'Folder Created',
  ARCHIVE_DOWNLOADED: 'Archive Downloaded',
  ARCHIVE_DOWNLOAD_FAILED: 'Archive Download Failed',
  FOLDER_RENAMED: 'Folder Renamed',
  FOLDER_DELETED: 'Folder Deleted',
  FOLDER_AI_REORGANIZE: 'Folder AI Reorganize',
  DOCUMENT_MOVED_TO_FOLDER: 'Document Moved To Folder',

  // ============================================
  // Letter Generation Events
  // ============================================  // Letters
  LETTER_GENERATION_STARTED: 'Letter Generation Started',
  LETTER_GENERATED: 'Letter Generated',
  LETTER_GENERATION_FAILED: 'Letter Generation Failed',
  LETTER_VIEWED: 'Letter Viewed',
  LETTER_CREATE_FORM_OPENED: 'Letter Create Form Opened',
  LETTER_DELETED: 'Letter Deleted',
  LETTER_TEXT_CHECK_SOURCES_CLICKED: 'Letter Text Check Sources Clicked',
  LETTER_TEXT_REGENERATE_CLICKED: 'Letter Text Regenerate Clicked',
  LETTER_REGENERATE_CLICKED: 'Letter Regenerate Clicked',
  LETTER_COPIED: 'Letter Copied',
  LETTER_DOWNLOAD_WORD_CLICKED: 'Letter Download Word Clicked',
  LETTER_OPEN_GOOGLE_DOCS_CLICKED: 'Letter Open Google Docs Clicked',
  DRAFTING_SAVED: 'Drafting Saved',
  DRAFTING_EXPORTED: 'Drafting Exported',
  DRAFTING_GENERATE_PETITION_CLICKED: 'Drafting Generate Petition Clicked',
  DRAFTING_EDIT_REVIEW_CLICKED: 'Drafting Edit Review Clicked',

  // ============================================
  // Petition Events
  // ============================================
  PETITION_GENERATED: 'Petition Generated',
  PETITION_UPDATED: 'Petition Updated',
  PETITION_EXPORTED: 'Petition Exported',
  PACKAGE_DOWNLOAD_WORD_CLICKED: 'Package Download Word Clicked',
  PACKAGE_TAB_DOWNLOAD_PDF_CLICKED: 'Package Tab Download PDF Clicked',
  PACKAGE_TAB_DOWNLOAD_WORD_CLICKED: 'Package Tab Download Word Clicked',
  PETITION_SECTION_APPROVED: 'Petition Section Approved',
  PETITION_SECTION_DISAPPROVED: 'Petition Section Disapproved',
  PETITION_SECTION_COPIED: 'Petition Section Copied',
  PETITION_SECTION_DELETED: 'Petition Section Deleted',
  PETITION_SECTION_ADDED: 'Petition Section Added',
  PETITION_SECTION_RENAMED: 'Petition Section Renamed',
  PETITION_SECTION_MOVED: 'Petition Section Moved',
  PETITION_TEXT_CHECK_SOURCES_CLICKED: 'Petition Text Check Sources Clicked',
  PETITION_TEXT_REGENERATE_CLICKED: 'Petition Text Regenerate Clicked',
  EXHIBIT_LIST_GENERATED: 'Exhibit List Generated',
  EXHIBIT_LIST_GENERATE_CLICKED: 'Exhibit List Generate Clicked',
  EXHIBIT_LIST_GENERATION_SUBMITTED: 'Exhibit List Generation Submitted',
  EXHIBIT_LIST_UPDATED: 'Exhibit List Updated',
  EXHIBIT_LIST_DOWNLOAD_INDEX_PDF_CLICKED:
    'Exhibit List Download Index PDF Clicked',
  EXHIBIT_LIST_DOWNLOAD_INDEX_WORD_CLICKED:
    'Exhibit List Download Index Word Clicked',
  EXHIBIT_LIST_DOWNLOAD_FULL_PDF_CLICKED:
    'Exhibit List Download Full PDF Clicked',

  // ============================================
  // Search & Filter Events
  // ============================================
  SEARCH_PERFORMED: 'Search Performed',
  FILTER_APPLIED: 'Filter Applied',
  FILTER_CLEARED: 'Filter Cleared',

  // ============================================
  // Knowledge Base Events
  // ============================================
  KNOWLEDGE_BASE_SEARCHED: 'Knowledge Base Searched',
  KNOWLEDGE_BASE_ARTICLE_VIEWED: 'Knowledge Base Article Viewed',
  KNOWLEDGE_BASE_FEEDBACK: 'Knowledge Base Feedback',

  // ============================================
  // Error Events
  // ============================================
  ERROR_OCCURRED: 'Error Occurred',
  API_ERROR: 'API Error',
  NETWORK_ERROR: 'Network Error',
  VALIDATION_ERROR: 'Validation Error',

  // ============================================
  // User Interaction Events
  // ============================================
  BUTTON_CLICKED: 'Button Clicked',
  MODAL_OPENED: 'Modal Opened',
  MODAL_CLOSED: 'Modal Closed',
  TOOLTIP_VIEWED: 'Tooltip Viewed',
} as const;

// Type for autocomplete
export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
